<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { applyShelterCurrency } from '@Helpers';
import { useAToast } from '@Atoms';
import type { CartAnimal, MedicalCost } from '../../../interfaces';
import { MEDICAL_COLLECTIONS } from '../../../constants';
import { CalculateCartMedicalCostsService } from '../../../services';
import useShoppingCartStore from '../../../store';

interface FlattenedItem extends MedicalCost {
  categoryLabel: string;
}

const props = withDefaults(
  defineProps<{
    animal: CartAnimal;
    canEditSummaryItems?: boolean;
    isEditingAnimals: boolean;
  }>(),
  {
    canEditSummaryItems: false,
  }
);

const { cartData } = storeToRefs(useShoppingCartStore());

const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();
const { t } = useI18n();

const TOAST_ERROR = {
  message: 'cart.toast.medicalCosts.error.message',
  title: 'cart.toast.medicalCosts.error.title',
  type: TOAST_TYPES.ERROR,
};

const expanded = ref(false);
const isEditingMedical = ref(false);
const medicalCostsLoading = ref(false);
const selectedType = ref('all');

const formattedTotal = computed(() => applyShelterCurrency(parseFloat(props.animal?.medical_total ?? '0') || 0));
const isEditingMedicalEffective = computed(() => props.isEditingAnimals || isEditingMedical.value);
const totalCount = computed(() => flattenedList.value.length);

const flattenedList = computed((): FlattenedItem[] => {
  const medicalCosts = props.animal?.medical_costs;
  if (!medicalCosts) return [];

  const list: FlattenedItem[] = [];
  const filterType = selectedType.value;

  MEDICAL_COLLECTIONS.forEach((collection) => {
    const invalidType = filterType !== 'all' && collection !== filterType;
    if (invalidType) return;

    const costs = medicalCosts[collection];
    if (!costs?.length) return;

    const categoryLabel = t(`cart.medicalCosts.types.${collection}`);
    costs.forEach((cost: MedicalCost) => {
      list.push({
        ...cost,
        type: collection,
        categoryLabel,
      } as FlattenedItem);
    });
  });

  return list;
});

const groupedByCategory = computed(() => {
  const list = flattenedList.value;
  const groups: { type: string; label: string; items: FlattenedItem[] }[] = [];
  let current: { type: string; label: string; items: FlattenedItem[] } | null = null;

  list.forEach((item) => {
    const isNewCategory = !current || current.type !== item.type;
    if (isNewCategory) {
      current = {
        type: item.type,
        label: item.categoryLabel,
        items: [],
      };
      groups.push(current);
    }

    current!.items.push(item);
  });

  return groups;
});

const hasMedicalCosts = computed(() => {
  const medicalCosts = props.animal?.medical_costs;
  if (!medicalCosts) return false;

  return Object.values(medicalCosts).some((costs: MedicalCost[] | undefined) => (costs?.length ?? 0) > 0);
});

const medicalToggleIcon = computed(() =>
  //prettier-ignore
  !hasMedicalCosts.value ? '' :
    expanded.value ? 'pi pi-chevron-down' 
    : 'pi pi-chevron-right'
);

// EVENTS
function onRefresh() {
  const animalId = props.animal?.animal_id;
  if (!animalId) return;

  medicalCostsLoading.value = true;

  CalculateCartMedicalCostsService({ animal_id: animalId })
    .then((data) => (cartData.value = data))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (medicalCostsLoading.value = false));
}

function onClickMedicalEditToggle() {
  isEditingMedical.value = !isEditingMedical.value;

  expanded.value = isEditingMedical.value;
}

function onClickMedicalToggle() {
  if (hasMedicalCosts.value) {
    expanded.value = !expanded.value;
    return;
  }

  push(`/shelter/animal-profile/${props.animal!.animal_id}/medical`);
}
</script>

<template>
  <div class="mt-2 text-sm">
    <Divider />

    <div class="flex h-8 flex-wrap items-center justify-between gap-2">
      <div class="flex items-center justify-center gap-1">
        <div class="relative">
          <Button
            plain
            text
            class="flex w-fit items-center justify-between gap-1 p-0 hover:bg-transparent"
            pt:icon="text-xs font-normal m-0"
            pt:label="text-sm font-normal"
            :icon="medicalToggleIcon"
            :label="`${$t('cart.title.medicalCosts')} (${totalCount})`"
            @click="onClickMedicalToggle"
          />

          <RouterLink
            v-if="!hasMedicalCosts"
            class="af-link absolute -bottom-3 left-0 w-[101px] text-xs font-medium"
            :to="`/shelter/animal-profile/${animal.animal_id}/medical`"
          >
            {{ $t('cart.medicalCosts.addMedicalCost') }}
          </RouterLink>
        </div>

        <AShoppingCartIconButton
          v-if="canEditSummaryItems && hasMedicalCosts"
          :icon="isEditingMedical ? 'pi pi-save' : 'pi pi-pencil'"
          @onClick="onClickMedicalEditToggle"
        />
      </div>

      <div
        v-if="!expanded"
        class="flex items-center gap-1"
      >
        <Button
          v-tooltip.top="$t('cart.medicalCosts.refreshButton.tooltip')"
          outlined
          class="af-button-thin border-none !py-0 text-xs text-gray-500"
          icon="pi pi-refresh"
          :loading="medicalCostsLoading"
          @click="onRefresh"
        />

        <span :class="['font-medium', isEditingMedicalEffective ? 'pr-12' : '']">
          {{ formattedTotal }}
        </span>
      </div>
    </div>

    <Transition
      enterActiveClass="transition duration-200 ease-[ease] origin-top"
      enterFromClass="opacity-0 scale-y-[0.95]"
      enterToClass="opacity-100 scale-y-100"
      leaveActiveClass="transition duration-200 ease-[ease] origin-top"
      leaveFromClass="opacity-100 scale-y-100"
      leaveToClass="opacity-0 scale-y-[0.95]"
    >
      <div
        v-show="expanded"
        class="border-surface-200 dark:border-surface-700 mt-2 space-y-3 pl-4"
      >
        <div class="space-y-3">
          <div
            v-for="{ type, label, items } in groupedByCategory"
            class="space-y-1"
            :key="type"
          >
            <p class="text-surface-600 dark:text-surface-400 text-xs font-semibold uppercase tracking-wide">
              {{ label }}
            </p>

            <AShoppingCartMedicalEdit
              v-for="cost in items"
              allowReceiptInclusionToggle
              :cost
              :canEditRetailCost="canEditSummaryItems"
              :isEditing="isEditingMedicalEffective"
              :key="cost._id"
            />
          </div>
        </div>

        <div class="border-surface-200 dark:border-surface-700 flex items-center justify-end gap-1 border-t pt-2">
          <Button
            v-tooltip.top="$t('cart.medicalCosts.refreshButton.tooltip')"
            outlined
            class="af-button-thin border-none !py-0 text-xs text-gray-500"
            icon="pi pi-refresh"
            :loading="medicalCostsLoading"
            @click="onRefresh"
          />

          <span :class="['font-semibold']">
            {{ formattedTotal }}
          </span>
        </div>
      </div>
    </Transition>
  </div>
</template>
